const $ = require('jquery');
const {debounce} = require('../../components/custom-lodash');

let prevInputValue;

/**
 * Handles errors during the address lookup process by updating the UI elements accordingly.
 *
 * @param {Object} container - The container element holding the address input and related UI elements.
 * @param {Object} addressInput - The address input element.
 */
function addressLookupError(container, addressInput) {
    const ulElement = container.find('.locations-list');
    const messageField = container.find('.messageField');
    const validIcon = container.find('.show-valid');
    const warningIcon = container.find('.show-warning');
    const additionalFields = container.nextAll('.additionalFields').first();
    const additionalAddress = container.nextAll('.additionalAddress').first();
    const showHiddenFields = container.next().find('.showHiddenFields');
    const hideFields = container.next().find('.hideFields');
    const mandatorySymbol = container.find('.required-indicator');
    const searchErrorMessage = messageField.data('search-error-message');

    // Update the message field with the error message
    messageField.text(`${searchErrorMessage}`);
    messageField.removeClass('hidden');

    // Update the icons to reflect the error state
    validIcon.addClass('hidden');
    warningIcon.removeClass('hidden');

    // Hide the locations list
    ulElement.addClass('hidden');

    // Show additional fields for manual address entry
    additionalFields.removeClass('hidden');
    additionalAddress.removeClass('hidden');

    // Hide and show relevant fields and indicators
    showHiddenFields.addClass('hidden');
    hideFields.addClass('hidden');
    addressInput.removeClass('required');
    mandatorySymbol.addClass('hidden');
}

/**
 * Handles the success response of the address lookup by updating the UI elements accordingly.
 *
 * @param {Object} container - The container element holding the address input and related UI elements.
 * @param {Object} addressInput - The address input element.
 * @param {boolean} haveOptions - Boolean indicating whether address options are available.
 */
function addressLookupSuccess(container, addressInput, haveOptions) {
    const ulElement = container.find('.locations-list');
    const messageField = container.find('.messageField');
    const additionalFields = container.nextAll('.additionalFields').first();
    const additionalAddress = container.nextAll('.additionalAddress').first();
    const showHiddenFields = container.next().find('.showHiddenFields');
    const hideFields = container.next().find('.hideFields');
    const mandatorySymbol = container.find('.required-indicator');

    if (haveOptions) {
        // If address options are available, show the list and update the UI elements accordingly
        // Show the locations list
        ulElement.removeClass('hidden');

        // hide the message field
        messageField.addClass('hidden');

        // Hide additional fields for manual address entry
        additionalFields.addClass('hidden');
        additionalAddress.addClass('hidden');

        // Hide and show relevant fields and indicators
        showHiddenFields.removeClass('hidden');
        hideFields.addClass('hidden');
        addressInput.addClass('required');
        mandatorySymbol.removeClass('hidden');
    } else {
        // If no address options match, show the appropriate message and update the UI elements
        const validIcon = container.find('.show-valid');
        const warningIcon = container.find('.show-warning');
        const searchNoMatch = messageField.data('search-no-match.message');

        // Update the message field with the warning message
        messageField.text(`${searchNoMatch}`);
        messageField.removeClass('hidden');

        // Update the icons to reflect the warning state
        validIcon.addClass('hidden');
        warningIcon.removeClass('hidden');

        // Hide the locations list
        ulElement.addClass('hidden');
    }
}

/**
 * Handles the scenario where the address lookup service is down by updating the UI elements accordingly.
 *
 * @param {Object} container - The container element holding the address input and related UI elements.
 * @param {Object} addressInput - The address input element.
 */
function addressLookupServerDown(container, addressInput) {
    const ulElement = container.find('.locations-list');
    const messageField = container.find('.messageField');
    const validIcon = container.find('.show-valid');
    const warningIcon = container.find('.show-warning');
    const additionalFields = container.nextAll('.additionalFields').first();
    const additionalAddress = container.nextAll('.additionalAddress').first();
    const showHiddenFields = container.next().find('.showHiddenFields');
    const hideFields = container.next().find('.hideFields');
    const mandatorySymbol = container.find('.required-indicator');
    const serverDownMessage = messageField.data('server-down-message');

    // Update message field with server down message
    messageField.text(`${serverDownMessage}`);
    messageField.removeClass('hidden');

    // Hide valid icon and show warning icon
    validIcon.addClass('hidden');
    warningIcon.removeClass('hidden');

    // Hide locations list
    ulElement.addClass('hidden');

    // Show additional fields for manual address entry
    additionalFields.removeClass('hidden');
    additionalAddress.removeClass('hidden');

    // Hide showHiddenFields link and show hideFields link
    showHiddenFields.addClass('hidden');
    hideFields.removeClass('hidden');

    // Adjust address input requirement and mandatory symbol visibility
    addressInput.removeClass('required');
    mandatorySymbol.addClass('hidden');
}

/**
 * Updates the design of the of the billing and shipping form by modifying input fields, hiding error messages, and toggling the fields like city,zipCode and address.
 *
 * @param {jQuery} container - The jQuery object representing the container element to update.
 * @param {jQuery} nextSibling - The jQuery object representing the next sibling element to start toggling classes from.
 */
function updateUIDesign(container, nextSibling) {
    const $addressInput = container.find('.addressSearch');
    const currentPlaceholder = $addressInput.attr('placeholder');
    const newPlaceholder = currentPlaceholder.replace('*', '');
    const classesToToggle = [];
    const additionalFields = container.nextAll('.additionalFields').first();
    const additionalAddress = container.nextAll('.additionalAddress').first();
    const formRow = container.find('.form-row');

    $addressInput.removeClass('required');
    container.find('.error-msg').addClass('hidden');
    container.find('.currencySymbolError').addClass('hidden');
    formRow.removeClass('validation-error');
    $addressInput.attr('placeholder', newPlaceholder.trim());
    container.find('.required-indicator').addClass('hidden');
    container.find('.locations-list').addClass('hidden');
    container.find('.messageField').addClass('hidden');

    if (additionalFields.hasClass('hidden')) {
        classesToToggle.push('additionalFields');
    }

    if (additionalAddress.hasClass('hidden')) {
        classesToToggle.push('additionalAddress');
    }

    // Toggle all classes in the array
    toggleHiddenClasses(nextSibling, classesToToggle);
}

/**
 * Fetches address suggestions from the server based on the provided input value and updates the options accordingly.
 *
 * @param {jQuery|HTMLElement} container - The container element from which to retrieve the URL endpoint for the AJAX request.
 * @param {string} inputValue - The value used to query the server for address suggestions.
 * @param {Function} callback - The callback function to execute once the request completes. It takes three arguments:
 */
function updateOptions(container, inputValue, callback) {
    const endpoint = $(container).data('url');
    if (endpoint) {
        $.ajax({
            url: endpoint,
            method: 'GET',
            data: {
                searchKey: inputValue
            },
            /**
             * Handles a successful response from the server.
             *
             * @param {Object} data - The response data from the server.
             * @param {boolean} data.success - Indicates whether the request was successful.
             * @param {Array} data.addressSuggestions - The list of address suggestions returned from the server.
             */
            success: data => {
                callback(null, data.success, data.addressSuggestions);
            },
            /**
             * Handles an error response from the server.
             *
             * @param {Object} data - The error response data from the server.
             * @param {Object} data.responseJSON - The JSON object containing error details.
             */
            error: data => {
                callback(data);
            }
        });
    }
}

/**
 * Toggle the 'hidden' class on elements with specified classes within the given sibling elements.
 *
 * @param {jQuery} nextSibling - The jQuery object representing the initial sibling element.
 * @param {string[]} classes - An array of class names to toggle visibility.
 */
function toggleHiddenClasses(nextSibling, classes) {
    let sibling = nextSibling;
    classes.forEach(className => {
        while (sibling.length && !sibling.hasClass(className)) {
            sibling = sibling.next();
        }
        if (sibling.length) {
            sibling.toggleClass('hidden');
        }
        // Reset sibling to nextSibling for the next class toggle
        sibling = nextSibling;
    });
}

/**
 * Updates the content of the provided message field with an SVG icon and a loading message.
 *
 * @param {jQuery} messageField - The jQuery object representing the message field to update.
 */
function updateMessageField(messageField) {
    const loadingMessage = messageField.data('loading-message');
    const svg = `
                <svg width="11" height="16" viewBox="0 0 11 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.1645 0.276665L6.12037 8.27666C6.04467 8.42642 6.04984 8.60306 6.13331 8.74834L10.1774 15.7487L10.6161 15.0011H0.505843L0.944549 15.7493L4.98867 8.74962C5.07278 8.60434 5.07731 8.4277 5.00161 8.27794L0.95749 0.277945L0.505843 1.00114H10.6161C10.8957 1.00114 11.1215 0.777145 11.1215 0.501305C11.1215 0.225465 10.895 0.00146484 10.6161 0.00146484H0.505843C0.129902 0.00146484 -0.114039 0.393145 0.0535489 0.725305L4.09767 8.7253L4.11061 8.25362L0.0664901 15.2539C-0.126333 15.5874 0.116961 16.0021 0.505196 16.0021H10.6155C11.0037 16.0021 11.247 15.5874 11.0542 15.2539L7.01008 8.25362L7.02367 8.7253L11.0678 0.725305C11.1927 0.478265 11.0911 0.178105 10.8413 0.0545848C10.5915 -0.0689352 10.2881 0.0309048 10.1632 0.277945L10.1645 0.276665ZM6.57137 4.50002L5.56002 6.99987L4.54867 4.50002H6.57073H6.57137ZM3.03325 14.0002L5.56131 10.0002L8.08808 14.0002H3.03261H3.03325Z" fill="black"/>
                </svg>
            `;
    messageField.html(`${svg}<span class="innerMessage">${loadingMessage}</span>`);
}

/**
 * Highlights the occurrences of a given input string within the address property of each object in an array of options.
 *
 * @param {string} input - The string to be highlighted within the addresses.
 * @param {Array<Object>} options - Array of objects where each object has an address property to be highlighted.
 * @returns {Array<Object>} A new array of objects where each address has the input string wrapped in a span with the class "highlight".
 */
function highlightAddresses(input, options) {
    const regex = new RegExp(`(${input})`, 'gi'); // Create a regular expression with the input string
    return options.map(option => {
        const highlightedAddress = option.address1.replace(regex, '<span class="highlight">$1</span>'); // Replace matching parts with highlight span
        return {...option, highlightedAddress}; // Return a new object with the highlighted address
    });
}

/**
 * Handles the input event by validating the input value, updating address suggestions, and adjusting UI elements based on the result.
 *
 * @param {jQuery|HTMLElement} container - The jQuery object or raw HTML element that contains data attributes for the search configuration and where options are fetched from.
 * @param {jQuery} addressInput - The jQuery object representing the address input field where the user types their search query.
 */
function handleInput(container, addressInput) {
    const ulElement = container.find('.locations-list');
    const messageField = container.find('.messageField');
    const inputValue = addressInput.val().trim();
    const searchMinCharacter = Math.floor($(container).data('search-min-char'));
    let haveOptions;

    messageField.removeClass('hidden');

    if (inputValue.length < searchMinCharacter) {
        const searchInitializationMessage = messageField.data('search-initialization-message');
        messageField.text(`${searchInitializationMessage}`);
        ulElement.addClass('hidden');
        return;
    }

    // If the input value is the same as the previous one, it uses the cached options.
    if (inputValue !== prevInputValue) {
        updateMessageField(messageField);
        prevInputValue = inputValue;

        updateOptions(container, inputValue, (error, success, options) => {
            if (error) {
                addressLookupError(container, addressInput);
                return;
            }

            if (success) {
                if (options.length !== 0) {
                    const addressesWithHighlights = highlightAddresses(inputValue, options);
                    ulElement.empty();

                    // Create and append li items to ul element
                    addressesWithHighlights.forEach(option => {
                        const address = option.address1.replace(option.city, '').trim();
                        const highlightedAddress = option.highlightedAddress || '';
                        const postalCode = option.postalCode || '';
                        const html = `${highlightedAddress} ${postalCode}`;
                        const li = $('<li></li>')
                            .html(html)
                            .attr('data-address', address)
                            .attr('data-city', option.city)
                            .attr('data-zip', option.postalCode);
                        ulElement.append(li);
                    });

                    haveOptions = true;
                    addressLookupSuccess(container, addressInput, haveOptions);
                } else {
                    haveOptions = false;
                    addressLookupSuccess(container, addressInput, haveOptions);
                }
            } else {
                addressLookupServerDown(container, addressInput);
            }
        });
    } else if (ulElement.find('li').length > 0) {
        messageField.addClass('hidden');
        ulElement.removeClass('hidden');
    } else {
        ulElement.addClass('hidden');
    }
}

/**
 * Handles the click event on a list item (li) to update various input fields and UI elements based on the selected option.
 *
 * @param {jQuery} li - The jQuery object representing the clicked list item.
 * @param {jQuery} address1 - The jQuery object representing the address input field.
 * @param {jQuery} cityInput - The jQuery object representing the city input field.
 * @param {jQuery} postalInput - The jQuery object representing the postal code input field.
 * @param {jQuery|HTMLElement} container - The jQuery object or raw HTML element that contains data attributes for the search configuration and where options are fetched from.
 */
function handleLiClick(li, address1, cityInput, postalInput, container) {
    const inputElement = container.find('.addressSearch');
    const messageField = container.find('.messageField');
    const additionalFields = container.nextAll('.additionalFields').first();
    const additionalAddress = container.nextAll('.additionalAddress').first();
    const showHiddenFields = container.next().find('.showHiddenFields');
    const hideFields = container.next().find('.hideFields');
    const validIcon = container.find('.show-valid');
    const warningIcon = container.find('.show-warning');

    // Store selected option value
    const selectedOption = li.text();
    const address = li.attr('data-address');
    const city = li.attr('data-city');
    const zip = li.attr('data-zip');

    // Populate selected option in input field
    inputElement.val(selectedOption);
    validIcon.removeClass('hidden');
    warningIcon.addClass('hidden');

    address1.val(address);
    if (address) {
        address1.addClass('filled-out');
    } else if (address1.hasClass('filled-out')) {
        address1.removeClass('filled-out');
    }

    cityInput.val(city);
    if (city) {
        cityInput.addClass('filled-out');
    } else if (cityInput.hasClass('filled-out')) {
        cityInput.removeClass('filled-out');
    }

    postalInput.val(zip);
    if (zip) {
        postalInput.addClass('filled-out');
    } else if (postalInput.hasClass('filled-out')) {
        postalInput.removeClass('filled-out');
    }

    if (address && city && zip) {
        additionalFields.addClass('hidden');
        showHiddenFields.removeClass('hidden');
        hideFields.addClass('hidden');
    } else {
        additionalFields.removeClass('hidden');
        showHiddenFields.addClass('hidden');
        hideFields.removeClass('hidden');
    }
    additionalAddress.removeClass('hidden');
    li.closest('.address-container').find('.locations-list').addClass('hidden');
    messageField.text('');
}

/**
 * A debounced function that handles the input click event for address-related fields.
 * It validates the input and updates the UI with error messages, icons, and address suggestions.
 * This function uses debouncing to limit the rate at which the input handler is invoked, improving performance.
 *
 * @param {Event} event - The click event object that triggered the function.
 */
const debouncedHandleClick = debounce(function () {
    const container = $(this).closest('.address-container');
    const messageField = container.find('.messageField');
    const errorMessage = container.find('.currencySymbolError');
    const warningIcon = container.find('.show-warning');
    const validIcon = container.find('.show-valid');

    if (this.validity.patternMismatch) {
        messageField.addClass('hidden');
        errorMessage.removeClass('hidden');
        validIcon.addClass('hidden');
        warningIcon.addClass('hidden');
    } else {
        errorMessage.addClass('hidden');
        handleInput(container, $(this));
    }
});

/**
 * Creates a debounced function that handles input changes for address-related fields.
 * This function validates the input, updates the UI elements based on the input value,
 * and handles address suggestions. It uses debouncing to limit the frequency of input handling.
 *
 * @param {number} debounceDelay - The delay in milliseconds to debounce the function.
 * @returns {Function} A debounced function that processes the input changes.
 */
function debouncedHandleInput(debounceDelay) {
    return debounce(function () {
        const container = $(this).closest('.address-container');
        const errorMessage = container.find('.currencySymbolError');
        const messageField = container.find('.messageField');
        const locationList = container.find('.locations-list');
        const warningIcon = container.find('.show-warning');
        const validIcon = container.find('.show-valid');

        locationList.addClass('hidden');
        if (this.validity.patternMismatch) {
            messageField.addClass('hidden');
            errorMessage.removeClass('hidden');
            validIcon.addClass('hidden');
            warningIcon.addClass('hidden');
        } else {
            validIcon.addClass('hidden');
            warningIcon.addClass('hidden');
            const addressForm = container.closest('.jsShippingAddressForm, .jsBillingAddressForm');
            errorMessage.addClass('hidden');

            if (addressForm.length > 0) {
                // Reset fields based on the type of address form
                if (addressForm.hasClass('jsShippingAddressForm')) {
                    addressForm
                        .find('input[id^="dwfrm_singleshipping_shippingAddress_addressFields_address1"]')
                        .val('');
                    addressForm.find('input[id^="dwfrm_singleshipping_shippingAddress_addressFields_city"]').val('');
                    addressForm.find('input[id^="dwfrm_singleshipping_shippingAddress_addressFields_postal"]').val('');
                } else if (addressForm.hasClass('jsBillingAddressForm')) {
                    addressForm.find('input[id^="dwfrm_singleshipping_billingAddress_addressFields_address1"]').val('');
                    addressForm.find('input[id^="dwfrm_singleshipping_billingAddress_addressFields_city"]').val('');
                    addressForm.find('input[id^="dwfrm_singleshipping_billingAddress_addressFields_postal"]').val('');
                }
            } else {
                // Fallback: Reset specific IDs if no form is found
                $('#dwfrm_profile_address_address1').val('');
                $('#dwfrm_profile_address_city').val('');
                $('#dwfrm_profile_address_postal').val('');
            }

            handleInput(container, $(this));
        }
    }, debounceDelay);
}

/**
 * Checks if any of the specified elements have a sibling with the class 'error-msg'.
 *
 * This function iterates over an array of element IDs, selects each element, and verifies if it has
 * a sibling `div` element with the class 'error-msg'. If at least one such sibling is found, the function
 * returns `true`; otherwise, it returns `false`.
 *
 * @param {string[]} ids - An array of strings representing the IDs of the elements to check. Each ID should be
 *                         prefixed with a hash symbol (`#`) to target elements by ID.
 * @returns {boolean} Returns `true` if any of the specified elements have a sibling with the class 'error-msg';
 *                    otherwise, returns `false`.
 */
function checkForErrorMsgSibling(ids) {
    let found = false;
    ids.forEach(function (id) {
        // Select the element by ID
        const element = $(id);

        // Check if the element exists and has a sibling with class 'error-msg'
        if (element.length > 0) {
            const siblingErrorMsg = element.siblings('div.error-msg');
            if (siblingErrorMsg.length > 0) {
                found = true;
            }
        }
    });
    return found;
}

/**
 * Main module for handling address form interactions and UI updates.
 *
 * This module manages the functionality for shipping and billing address forms, including:
 * - Checking for error messages related to address fields.
 * - Handling input events with debounce to prevent excessive server requests.
 * - Managing UI elements such as showing and hiding fields based on user actions.
 * - Handling clicks on list items within address dropdowns to populate address fields.
 * - Toggling visibility of additional fields and updating input field properties.
 *
 *  @param {Function} updateShippingMethodList - Callback function to update the list of available shipping methods
 *                                              based on the selected address or other criteria.
 */
function init(updateShippingMethodList) {
    const shippingContainer = $('.jsShippingAddressForm').find('.address-container');
    const billingContainer = $('.jsBillingAddressForm').find('.address-container');
    const shippingAddress1 = document.getElementById(
        'dwfrm_singleshipping_shippingAddress_addressFields_address1'
    ).value;
    const shippingCity = document.getElementById('dwfrm_singleshipping_shippingAddress_addressFields_city').value;
    const shippingPostal = document.getElementById('dwfrm_singleshipping_shippingAddress_addressFields_postal').value;
    const billingAddress1 = document.getElementById('dwfrm_singleshipping_billingAddress_addressFields_address1').value;
    const billingCity = document.getElementById('dwfrm_singleshipping_billingAddress_addressFields_city').value;
    const billingPostal = document.getElementById('dwfrm_singleshipping_billingAddress_addressFields_postal').value;
    const shippingIds = [
        '#dwfrm_singleshipping_shippingAddress_addressFields_address1',
        '#dwfrm_singleshipping_shippingAddress_addressFields_city',
        '#dwfrm_singleshipping_shippingAddress_addressFields_postal'
    ];

    const billingIds = [
        '#dwfrm_singleshipping_billingAddress_addressFields_address1',
        '#dwfrm_singleshipping_billingAddress_addressFields_city',
        '#dwfrm_singleshipping_billingAddress_addressFields_postal'
    ];

    const isShippingErrorPresent = checkForErrorMsgSibling(shippingIds);
    const isBillingErrorPresent = checkForErrorMsgSibling(billingIds);
    const debounceDelay = Math.floor($(shippingContainer).data('address-search-delay'));

    if (shippingAddress1 && shippingCity && shippingPostal) {
        $('.jsShippingAddressForm').find('.link-container').find('.showHiddenFields').addClass('hidden');
        $('.jsShippingAddressForm').find('.link-container').find('.hideFields').removeClass('hidden');
        const nextSibling = $('.jsShippingAddressForm').find('.link-container').next();
        updateUIDesign(shippingContainer, nextSibling);
    }

    if (billingAddress1 && billingCity && billingPostal) {
        $('.jsBillingAddressForm').find('.link-container').find('.showHiddenFields').addClass('hidden');
        $('.jsBillingAddressForm').find('.link-container').find('.hideFields').removeClass('hidden');
        const nextSibling = $('.jsBillingAddressForm').find('.link-container').next();
        updateUIDesign(billingContainer, nextSibling);
    }

    if (isShippingErrorPresent) {
        $('.jsShippingAddressForm').find('.link-container').find('.showHiddenFields').addClass('hidden');
        $('.jsShippingAddressForm').find('.link-container').find('.hideFields').removeClass('hidden');
        const nextSibling = $('.jsShippingAddressForm').find('.link-container').next();
        updateUIDesign(shippingContainer, nextSibling);
    }

    if (isBillingErrorPresent) {
        $('.jsBillingAddressForm').find('.link-container').find('.showHiddenFields').addClass('hidden');
        $('.jsBillingAddressForm').find('.link-container').find('.hideFields').removeClass('hidden');
        const nextSibling = $('.jsBillingAddressForm').find('.link-container').next();
        updateUIDesign(billingContainer, nextSibling);
    }
    /**
     * Document click event handler to hide the locations list when clicking outside the address input or locations list.
     *
     * @param {jQuery.Event} event - The jQuery event object representing the click event.
     */
    $(document).click(function (event) {
        const target = $(event.target);
        const input = $('.addressSearch');
        const container = input.closest('.address-container');
        const dropDownList = container.find('.locations-list');
        const linkContainer = container.siblings('.link-container').find('.links');
        const messageField = container.find('.messageField');

        if (
            !target.is(input) &&
            !target.closest(input).length &&
            !target.is(dropDownList) &&
            !target.closest(dropDownList).length &&
            !target.is(linkContainer) &&
            !target.closest(linkContainer).length
        ) {
            dropDownList.addClass('hidden');
            messageField.addClass('hidden');
        }
    });

    $(document).on('input', '.addressSearch', debouncedHandleInput(debounceDelay));

    $(document).on('click', '.addressSearch', debouncedHandleClick);

    /**
     * Event handler for clicking on list items within the locations list.
     * This handler determines whether the clicked item belongs to a shipping or billing address form,
     * and then extracts relevant input fields to pass to the `handleLiClick` function.
     */
    $(document).on('click', '.locations-list li', function () {
        const container = $(this).closest('.address-container');
        const addressForm = container.closest('.jsShippingAddressForm, .jsBillingAddressForm');
        let address1;
        let cityInput;
        let postalInput;

        // Determine if it's shipping or billing address section based on parent class
        if (addressForm.hasClass('jsShippingAddressForm')) {
            address1 = addressForm.find('input[id^="dwfrm_singleshipping_shippingAddress_addressFields_address1"]');
            cityInput = addressForm.find('input[id^="dwfrm_singleshipping_shippingAddress_addressFields_city"]');
            postalInput = addressForm.find('input[id^="dwfrm_singleshipping_shippingAddress_addressFields_postal"]');
        } else if (addressForm.hasClass('jsBillingAddressForm')) {
            address1 = addressForm.find('input[id^="dwfrm_singleshipping_billingAddress_addressFields_address1"]');
            cityInput = addressForm.find('input[id^="dwfrm_singleshipping_billingAddress_addressFields_city"]');
            postalInput = addressForm.find('input[id^="dwfrm_singleshipping_billingAddress_addressFields_postal"]');
        }

        // Call the function with the selected elements
        handleLiClick($(this), address1, cityInput, postalInput, container);
        updateShippingMethodList();
    });

    /**
     * Event handler for the click event on elements with the class `showHiddenFields`.
     * Performs various actions such as hiding the current link, showing a related link, updating input field properties,
     * and toggling the visibility of the next sibling element with the classes `additionalFields, additionalAddress`.
     *
     */
    $('.showHiddenFields').on('click', function (event) {
        event.preventDefault();
        const container = $(this).closest('.link-container').prev('.address-container');
        const nextSibling = $(this).parent().next();

        $(this).addClass('hidden');
        $(this).next('.hideFields').removeClass('hidden');
        updateUIDesign(container, nextSibling);
    });

    /**
     * Event handler for the click event on elements with the class `hideFields`.
     * Performs various actions such as hiding the current link, showing a related link, updating input field properties,
     * and toggling the visibility of the next sibling element with the classes `additionalFields, additionalAddress`.
     */
    $('.hideFields').on('click', function (event) {
        event.preventDefault();

        const prevSibling = $(this).prev('.showHiddenFields');
        const container = $(this).closest('.link-container').prev('.address-container');
        const $addressInput = container.find('.addressSearch');
        const $errorField = container.find('.error-msg');
        const currentPlaceholder = $addressInput.attr('placeholder');
        const newPlaceholder = `${currentPlaceholder} *`;
        const nextSibling = $(this).parent().next();
        const classesToToggle = ['additionalFields'];
        const validIcon = container.find('.show-valid');

        $(this).addClass('hidden');
        prevSibling.removeClass('hidden');
        $errorField.removeClass('hidden');
        $addressInput.addClass('required');
        // Change the placeholder attribute of the input field
        $addressInput.attr('placeholder', newPlaceholder.trim());
        container.find('.required-indicator').removeClass('hidden');
        container.find('.locations-list').addClass('hidden');
        container.find('.messageField').addClass('hidden');
        if (validIcon.hasClass('hidden')) {
            classesToToggle.push('additionalAddress');
        }

        // Toggle all classes in the array
        toggleHiddenClasses(nextSibling, classesToToggle);
    });
}

module.exports = {
    init,
    checkForErrorMsgSibling,
    updateUIDesign,
    debouncedHandleInput,
    debouncedHandleClick,
    handleLiClick,
    toggleHiddenClasses,
    handleInput
};
